'use client';

import { VStack, token } from '@terra/ui-styled-system';
import { formatDistanceToNow } from 'date-fns';
import { useTranslations } from 'next-intl';
import { Badge } from '../../components/badge';
import { Tooltip } from '../../components/styled/tooltip';
import { MetaDefault } from '../../components/typography';
import {
  type GatheredArtifactItem,
  GatheredArtifactsTooltipContent,
} from './GatheredArtifactsPanel';

type CredentialVerificationBadgeStatus =
  | 'PENDING'
  | 'VERIFYING'
  | 'VERIFIED'
  | 'FAILED';

type CredentialVerifiedBadgeProps = {
  status: CredentialVerificationBadgeStatus;
  verifiedAt?: string | Date | null;
  verificationArtifacts?: GatheredArtifactItem[];
  showSessionTooltip?: boolean;
};

type VerificationBadgeConfig = {
  label: string;
  bg?: string;
  color?: string;
};

function getVerificationBadgeConfig({
  status,
  labels,
}: {
  status: CredentialVerificationBadgeStatus;
  labels: {
    pending: string;
    verified: string;
    verificationFailed: string;
  };
}): VerificationBadgeConfig {
  if (status === 'VERIFYING') {
    return {
      label: labels.pending,
      bg: token('colors.bg.accent.light'),
      color: token('colors.fg.accent.muted'),
    };
  }

  if (status === 'VERIFIED') {
    return {
      label: labels.verified,
      bg: token('colors.bg.neutral.success'),
    };
  }

  if (status === 'FAILED') {
    return {
      label: labels.verificationFailed,
      bg: token('colors.bg.neutral.error'),
    };
  }

  return {
    label: labels.pending,
  };
}

export function CredentialVerifiedBadge({
  status,
  verifiedAt,
  verificationArtifacts = [],
  showSessionTooltip,
}: CredentialVerifiedBadgeProps) {
  const t = useTranslations('designSystem.applicationCredentials');

  const verificationBadge = getVerificationBadgeConfig({
    status,
    labels: {
      pending: t('pending'),
      verified: t('verified'),
      verificationFailed: t('verificationFailed'),
    },
  });

  const hasTooltipContent =
    showSessionTooltip &&
    status === 'VERIFIED' &&
    verificationArtifacts.length > 0;

  const showVerifiedAt = status === 'VERIFIED' && !!verifiedAt;

  const badge = (
    <Badge
      variant="fill"
      size="sm"
      bg={verificationBadge.bg}
      color={verificationBadge.color}
      flexShrink={0}
    >
      {verificationBadge.label}
    </Badge>
  );

  const content = (
    <VStack gap={0.5} alignItems="flex-end" flexShrink={0}>
      {badge}
      {showVerifiedAt ? (
        <MetaDefault color="fg.muted" whiteSpace="nowrap">
          {formatDistanceToNow(new Date(verifiedAt), { addSuffix: false })}{' '}
          {t('ago')}
        </MetaDefault>
      ) : null}
    </VStack>
  );

  if (!hasTooltipContent) {
    return content;
  }

  return (
    <Tooltip
      maxW="420px"
      allowOverflow
      customContent={
        <GatheredArtifactsTooltipContent
          artifacts={verificationArtifacts}
          hideCopy={!showSessionTooltip}
        />
      }
    >
      {content}
    </Tooltip>
  );
}
